import { useState, FormEvent } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6jscfqpaf6ievb71kf0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Top Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-16 pb-12 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/5a784f7f-0b3e-4f40-a8cc-c4e334701369.png" 
              alt="The Grand Salon Society" 
              className="h-10 w-auto"
            />
            <span className="font-serif text-xl">The Grand Salon Society</span>
          </div>
          <p className="text-gray-400 max-w-md">
            Reviving the art of private salon performance in Nashville.
          </p>
        </div>

        {/* Middle Section */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Experience */}
          <div>
            <h4 className="font-semibold text-white mb-4">Experience</h4>
            <ul className="space-y-3">
              <li>
                <a href="#experience" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  The Prelude Salon
                </a>
              </li>
              <li>
                <a href="#experience" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  The Cadenza Salon
                </a>
              </li>
              <li>
                <a href="#experience" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  The Grand Salon
                </a>
              </li>
              <li>
                <a href="#experience" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  Patron's Conservatory
                </a>
              </li>
            </ul>
          </div>

          {/* About */}
          <div>
            <h4 className="font-semibold text-white mb-4">About</h4>
            <ul className="space-y-3">
              <li>
                <a href="#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  Our Philosophy
                </a>
              </li>
              <li>
                <a href="#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  The Artists
                </a>
              </li>
              <li>
                <a href="#service-areas" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  Service Areas
                </a>
              </li>
              <li>
                <a href="#booking" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold text-white mb-4">Resources</h4>
            <ul className="space-y-3">
              <li>
                <a href="#booking" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  Hosting Guide
                </a>
              </li>
              <li>
                <a href="#experience" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  Repertoire Examples
                </a>
              </li>
              <li>
                <a href="#booking" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  FAQs
                </a>
              </li>
              <li>
                <a href="#booking" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  Book Consultation
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-semibold text-white mb-4">Stay Connected</h4>
            <p className="text-gray-400 text-sm mb-4 leading-relaxed">
              Join our mailing list for exclusive salon invitations
            </p>
            <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent outline-none text-sm text-white placeholder-gray-500"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center justify-center w-10 h-10 bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-line text-white"></i>
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-green-400 text-xs mt-2">Subscribed successfully!</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-400 text-xs mt-2">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid md:grid-cols-2 gap-8 items-center pt-12 border-t border-gray-800">
          <div className="w-full h-48 rounded-2xl overflow-hidden">
            <img 
              src="https://public.readdy.ai/ai/img_res/d19c50a2ad6ae6f5f277d1eecb54fdc2.jpg" 
              alt="Salon Gathering"
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div>
            <h3 className="font-serif text-4xl text-white mb-6 leading-tight">
              Elevate Your<br />Private<br />Gatherings
            </h3>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer">
                Instagram
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer">
                Facebook
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer">
                LinkedIn
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-center">
          <p className="text-amber-500 text-sm tracking-[0.2em] uppercase font-medium mb-3">
            Exclusively Conservatory-Trained Musicians. Without Exception.
          </p>
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} The Grand Salon Society. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}